import type { ReactNode } from 'react';

import { LogoMark } from '@/components/LogoMark/LogoMark';

import styles from './AuthScreen.module.css';

export interface AuthScreenProps {
  /** Which deployment this build talks to, e.g. `staging`. */
  environment?: string | null;
  children: ReactNode;
}

const STEPS = [
  { step: 'uploaded', says: 'job_01J9Q4…', done: true },
  { step: 'extracted', says: '2 entities · 1 edge', done: true },
  { step: 'person', says: 'Ada Byrne' },
  { step: 'employer', says: 'Acme Ltd' },
  { step: 'edge', says: 'Ada Byrne → employed by → Acme Ltd' },
];

/**
 * The split sign-in layout: the form column on the left, a decorative panel on
 * the right that says what this place is. The panel drops away on narrow screens.
 */
export function AuthScreen({ environment, children }: AuthScreenProps) {
  return (
    <div className={styles.screen}>
      <div className={styles.column}>
        <div className={styles.brand}>
          <span className={styles.mark}>
            <LogoMark size={30} />
          </span>
          <span className={styles.wordmark}>LendWell</span>
          <span className={styles.badge}>Developers</span>
          {environment ? <span className={styles.env}>{environment}</span> : null}
        </div>
        <div className={styles.content}>{children}</div>
        <p className={styles.footnote}>
          Direct access to the LendWell platform, for developers and account holders.
        </p>
      </div>

      <aside className={styles.art} aria-hidden="true">
        <div className={styles.glow} />
        <div className={styles.grid} />
        <div className={styles.artInner}>
          <span className={styles.artEyebrow}>Developer platform</span>
          <p className={styles.artTitle}>
            Build on LendWell,
            <br />
            one token away.
          </p>
          <div className={styles.terminal}>
            <div className={styles.terminalBar}>
              <span />
              <span />
              <span />
              <em>lendwell — extract</em>
            </div>
            <pre className={styles.terminalBody}>
              <span className={styles.prompt}>$</span> lendwell extract payslip.pdf{'\n'}
              {STEPS.map(({ step, says, done }, index) => (
                <span
                  className={styles.hop}
                  key={step}
                  style={{ animationDelay: `${700 + index * 300}ms` }}
                >
                  {done ? <span className={styles.tick}>✓</span> : ' '}{' '}
                  {done ? step.padEnd(11) : `  ${step.padEnd(9)}`}
                  <span className={styles.dim}>{says}</span>
                  {'\n'}
                </span>
              ))}
              <span className={styles.prompt}>$</span> <span className={styles.caret} />
            </pre>
          </div>
          <div className={styles.chips}>
            <span>Submit documents</span>
            <span>Track jobs</span>
            <span>Read results</span>
          </div>
        </div>
      </aside>
    </div>
  );
}
